import { Box, ListItem, paletteColors, Rating, Stack, Typography } from '@evaneos/b2b-ui';

import FlightTakeoffIcon from '@evaneos/b2b-ui/icons/FlightTakeoff';
import FilledCircleIcon from '@evaneos/b2b-ui/icons/Circle';
import EmptyCircleIcon from '@evaneos/b2b-ui/icons/CircleOutlined';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { deprecated_FFB200 } from '../../../styles/deprecated-colors';

import { DisplayDossierOverview } from '../../../resources/dossier/types';
import { RepeaterBadge } from '../../../design-system/RepeaterBadge/RepeaterBadge';
import { Avatar } from '../../../resources/agent/components/Avatar';
import { useDisplayedAgentFromFilter } from '../../../resources/filters/hooks';
import { useTrackEventAmplitude } from '../../../shared/analytics';
import { getElapsedMinOrHourSince } from '../../../shared/utilities/duration';

import { ElapsedTimeChip } from './ElapsedTimeChip';

type Props = {
    dossier: DisplayDossierOverview;
};

export const NewRequestItem = ({ dossier }: Props) => {
    const { formatMessage, formatDate } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const { project, rating, requestDate, isDuplicate, requesterIsRepeater, agentId } = dossier;
    const assignedAgent = useDisplayedAgentFromFilter(agentId);
    const now = new Date();

    const duration = getElapsedMinOrHourSince(requestDate, now);
    const isOverdue = duration.unit === 'hour' && duration.value >= 24;

    return (
        <ListItem
            sx={{
                height: '94px',
                marginBottom: '0',
                '&:hover': { background: paletteColors.neutral[0] },
                '&:focus-within': { background: paletteColors.neutral[0] },
                '&:not(:last-child)': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                },
            }}
            disablePadding
        >
            <Box
                component={Link}
                to={{
                    pathname: `/dossier/${dossier.id}/info`,
                }}
                onClick={() => {
                    trackEvent((ampli, defaultProperties) => {
                        ampli.newFileClicked({
                            ...defaultProperties,
                            request_id: parseInt(dossier.id),
                            source: 'home',
                            is_overdue: isOverdue,
                        });
                    });
                }}
                data-testid={`new-request-item-${dossier.id}`}
                sx={{
                    px: 3,
                    py: 2,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Box minWidth={0} display="flex" flexDirection="column" gap={0.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <ElapsedTimeChip now={now} requestDate={requestDate} />
                        <Typography
                            variant="h4"
                            component={'p'}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {project.requesterName}
                        </Typography>
                        {requesterIsRepeater && <RepeaterBadge size={20} />}
                        <Rating
                            value={rating}
                            readOnly
                            size="small"
                            icon={
                                <FilledCircleIcon
                                    sx={{ fontSize: '10px', color: deprecated_FFB200 }}
                                />
                            }
                            emptyIcon={
                                <EmptyCircleIcon
                                    sx={{ fontSize: '10px', color: deprecated_FFB200 }}
                                />
                            }
                            sx={{ gap: '3px' }}
                        />
                    </Stack>
                    <Stack direction="row" alignItems="center">
                        <FlightTakeoffIcon
                            fontSize="small"
                            htmlColor={paletteColors.neutral[700]}
                            sx={{ mr: 1 }}
                        />
                        <Typography variant="body2" color="textSecondary">
                            {project.expectedArrivalDate
                                ? formatDate(project.expectedArrivalDate, {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                  })
                                : '——'}
                        </Typography>
                        {isDuplicate && (
                            <>
                                <Typography variant="body1" color="textSecondary" mx={0.5}>
                                    •
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color={paletteColors.red[800]}
                                >
                                    {formatMessage({ id: 'homepage.new_request.duplicate' })}
                                </Typography>
                            </>
                        )}
                    </Stack>
                </Box>
                {assignedAgent && (
                    <Box marginLeft="auto" paddingLeft={2}>
                        <Avatar name={assignedAgent.name} photoUrl={assignedAgent.photoUrl} />
                    </Box>
                )}
            </Box>
        </ListItem>
    );
};
